/**
* functions common matomo
*
* @category   Javascript
* @author     Jakob Ohlsen <jakob.ohlsen@gmail.com>
* @copyright  2025 Jakob Ohlsen
*/
import { delayedFunction } from '@ts/common/timeout';
/**
 * Reads the <body data-matomo> payload. Returns null when the attribute is
 * absent or unparseable - callers then do nothing.
 */
export function readMatomoData() {
    if (!document.body.hasAttribute('data-matomo')) {
        return null;
    }
    try {
        return JSON.parse(document.body.getAttribute('data-matomo'));
    }
    catch (error) {
        console.warn('data-matomo is not valid JSON', error);
        return null;
    }
}
/**
 * Whether initializeMatomo() will really load matomo.js for this page view.
 * Single source of truth for that gate: the opt-out control below has to agree
 * with handleVisit() in page-defaults.ts, or it ends up offering a switch for a
 * tracker that was never loaded.
 */
export function isMatomoActive() {
    return readMatomoData()?.[0] === true;
}
export function initializeMatomo(visitorId, userId, pings) {
    const _paq = window._paq = window._paq || [];
    /* tracker methods like "setCustomDimension" should be called before "trackPageView" */
    // _paq.push(["setDoNotTrack", true]); // would enforce do not track option client side - not needed - using server side settings
    // _paq.push(['resetUserId']);
    if (visitorId) {
        // console.log('setVisitorId', visitorId)
        // _paq.push(['setVisitorId', visitorId]);
        _paq.push(['appendToTrackingUrl', 'cid=' + visitorId]);
    }
    if (userId) {
        // console.log('setUserId', userId)
        _paq.push(['setUserId', userId]);
    }
    _paq.push(["disableCookies"]);
    _paq.push(['trackPageView']);
    _paq.push(['enableLinkTracking']);
    const matomoPushElements = document.querySelectorAll('[data-matomo-push]');
    matomoPushElements.forEach(function (currentValue) {
        _paq.push(JSON.parse(currentValue.dataset.matomoPush));
    });
    (function () {
        const u = "//analytics.ai-diagnostics.net/";
        _paq.push(['setTrackerUrl', u + 'matomo.php']);
        _paq.push(['setSiteId', '1']);
        const d = document, g = d.createElement('script'), s = d.getElementsByTagName('script')[0];
        g.async = true;
        g.src = u + 'matomo.js';
        s.parentNode.insertBefore(g, s);
        g.addEventListener('load', function () {
            // console.log('initializedMatomo load event');
            const initializedMatomoEvent = new Event('initializedMatomo');
            document.dispatchEvent(initializedMatomoEvent);
        });
        // A blocked or failed request fires `error` on the element. The opt-out
        // control listens for this so it can say "we can neither read nor change
        // your setting" straight away instead of waiting out its backstop timer.
        g.addEventListener('error', function () {
            document.dispatchEvent(new Event('failedMatomo'));
        });
    })();
    if (pings) {
        delayedFunction(pings, (t) => {
            // console.log(`Ping called after ${t/1000} seconds`)
            const _paq = window._paq = window._paq || [];
            _paq.push(['ping']);
        });
    }
}
export function logMatomo(visitorId, userId) {
    if (visitorId)
        console.log('logMatomo - v', visitorId);
    if (userId)
        console.log('logMatomo - u', userId);
    const matomoPushElements = document.querySelectorAll('[data-matomo-push]');
    matomoPushElements.forEach(function (currentValue) {
        console.log(JSON.parse(currentValue.dataset.matomoPush));
    });
}
/** Backstop for "matomo.js never arrived and never fired `error` either". */
const UNAVAILABLE_AFTER_MS = 10000;
let currentOptOut = null;
/** All-or-nothing: the seven ids are the contract with privacy-policy.blade.php. */
function queryOptOutElements() {
    const input = document.getElementById('optOutMatomo');
    const labelPending = document.getElementById('optOutMatomoLabelPending');
    const labelIn = document.getElementById('optOutMatomoLabelIn');
    const labelOut = document.getElementById('optOutMatomoLabelOut');
    const labelInactive = document.getElementById('optOutMatomoLabelInactive');
    const noteInactive = document.getElementById('optOutMatomoNoteInactive');
    const noteUnavailable = document.getElementById('optOutMatomoNoteUnavailable');
    if (input === null || labelPending === null || labelIn === null || labelOut === null
        || labelInactive === null || noteInactive === null || noteUnavailable === null) {
        console.error('matomo opt-out: control markup not found');
        return null;
    }
    return { input, labelPending, labelIn, labelOut, labelInactive, noteInactive, noteUnavailable };
}
/**
 * Wires up the tracking opt-out switch. Call this from a page entry's module
 * body - see the listener-registration comment below.
 */
export function initMatomoOptOut(options = {}) {
    const el = queryOptOutElements();
    if (el === null) {
        return null;
    }
    // A second init must never leave the first one's click handler bound.
    if (currentOptOut !== null) {
        currentOptOut.destroy();
    }
    // Destructured because the handlers below are hoisted function declarations,
    // for which TypeScript discards the null-narrowing of `el`.
    const { input, labelPending, labelIn, labelOut, labelInactive, noteInactive, noteUnavailable } = el;
    const unavailableAfterMs = options.unavailableAfterMs ?? UNAVAILABLE_AFTER_MS;
    let state = 'pending';
    let timer;
    let clickBound = false;
    function show(element, visible) {
        element.classList.toggle('hidden', !visible);
    }
    // A total function of (state, input.checked): every call sets all six
    // visibilities, so no state can leave a stale span showing. It never writes
    // text - all strings live in privacy-policy.blade.php - and never touches
    // input.checked; only the tracker readback does that.
    function applyState(next) {
        state = next;
        if (next !== 'pending') {
            clearTimeout(timer);
            timer = undefined;
        }
        input.disabled = next !== 'active';
        show(labelPending, next === 'pending');
        show(labelIn, next === 'active' && input.checked);
        show(labelOut, next === 'active' && !input.checked);
        show(labelInactive, next === 'inactive' || next === 'unavailable');
        show(noteInactive, next === 'inactive');
        show(noteUnavailable, next === 'unavailable');
    }
    function pushPaq(command) {
        window._paq = window._paq || [];
        window._paq.push(command);
    }
    // The tracker is authoritative. Render the label INSIDE the queued callback:
    // assigning .checked from script fires no change event, so rendering it
    // anywhere else can (and did) leave the label describing the previous state.
    function syncFromTracker() {
        pushPaq([
            function () {
                input.checked = !this.isUserOptedOut();
                applyState('active');
            },
        ]);
    }
    // `checked` means tracking is ON, i.e. NOT opted out. It is only meaningful
    // in the 'active' state.
    function onClick() {
        if (state !== 'active') {
            return; // belt and braces: a disabled input dispatches no click
        }
        const optIn = input.checked; // the browser toggled it before click fired
        applyState('active'); // optimistic label flip, so no change listener is needed
        pushPaq([optIn ? 'forgetUserOptOut' : 'optUserOut']);
        syncFromTracker(); // authoritative re-read, queued right behind the command
    }
    function onInitialized() {
        if (!clickBound) {
            clickBound = true;
            input.addEventListener('click', onClick);
        }
        // The only path into 'active': the tracker has to answer first.
        syncFromTracker();
    }
    function onFailed() {
        if (state === 'pending') {
            applyState('unavailable');
        }
    }
    function armUnavailableTimer() {
        timer = setTimeout(function () {
            timer = undefined;
            if (state === 'pending') {
                applyState('unavailable');
            }
        }, unavailableAfterMs);
    }
    function destroy() {
        clearTimeout(timer);
        timer = undefined;
        document.removeEventListener('initializedMatomo', onInitialized);
        document.removeEventListener('failedMatomo', onFailed);
        window.removeEventListener('load', armUnavailableTimer);
        input.removeEventListener('click', onClick);
        clickBound = false;
        if (currentOptOut?.destroy === destroy) {
            currentOptOut = null;
        }
    }
    // Listeners FIRST, and initMatomoOptOut() must be called from the entry's
    // module body. Page entries are deferred, so the DOM is parsed and
    // page-defaults' own `load` listener has not run yet. Moving this call into
    // a load handler would register these listeners after page-defaults calls
    // initializeMatomo(), and it would then only still work by the accident that
    // script loading is async.
    document.addEventListener('initializedMatomo', onInitialized);
    document.addEventListener('failedMatomo', onFailed);
    if (isMatomoActive()) {
        applyState('pending');
        // handleVisit() only runs on `load`, and its slow branch awaits
        // getPrints() plus POST /api/v1/vi before initializeMatomo() is called at
        // all - so the backstop is anchored to `load`, not to init.
        if (document.readyState === 'complete') {
            armUnavailableTimer();
        }
        else {
            window.addEventListener('load', armUnavailableTimer, { once: true });
        }
    }
    else {
        // Staff account or non-production: matomo.js is never loaded, so there is
        // no opt-out setting to read or change. Say so, rather than offering a
        // switch that silently does nothing.
        applyState('inactive');
    }
    currentOptOut = { getState: () => state, destroy };
    return currentOptOut;
}
